import { useState, useEffect } from 'react'
import { supabase } from './supabase'

export default function OneVOne({ session, onBack }) {
  const [view, setView] = useState('list')
  const [bets, setBets] = useState([])
  const [friends, setFriends] = useState([])
  const [profiles, setProfiles] = useState({})
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState({})
  const [toast, setToast] = useState(null)
  const [pending, setPending] = useState({}) // tracks double-tap state per bet+action

  // Create form
  const [description, setDescription] = useState('')
  const [wager, setWager] = useState('')
  const [settleBy, setSettleBy] = useState('')
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [opponent, setOpponent] = useState(null)
  const [saving, setSaving] = useState(false)

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => { loadAll() }, [session])

  // Poll every 15s for disputed timer updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (bets.some(b => b.status === 'disputed')) loadAll()
    }, 15000)
    return () => clearInterval(interval)
  }, [bets])

  const loadAll = async () => {
    setLoading(true)
    const { data: b } = await supabase
      .from('one_v_one')
      .select('*')
      .or(`created_by.eq.${session.user.id},opponent_id.eq.${session.user.id}`)
      .order('created_at', { ascending: false })
    // Filter out pending bets where current user is the opponent — those belong in Invites
    const filtered = (b || []).filter(bet => !(bet.status === 'pending' && bet.opponent_id === session.user.id))
    setBets(filtered)

    const { data: f } = await supabase
      .from('friends').select('*')
      .or(`user_id.eq.${session.user.id},friend_id.eq.${session.user.id}`)
      .eq('status', 'accepted')
    setFriends(f || [])

    const ids = new Set()
    b?.forEach(bet => { ids.add(bet.created_by); ids.add(bet.opponent_id) })
    f?.forEach(fr => { ids.add(fr.user_id); ids.add(fr.friend_id) })
    if (ids.size > 0) {
      const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name, username').in('id', [...ids])
      const pmap = {}
      prof?.forEach(p => { pmap[p.id] = p })
      setProfiles(pmap)
    }
    setLoading(false)
  }

  const handleSearch = async (q) => {
    setSearch(q)
    if (q.length < 2) { setSearchResults([]); return }
    const { data } = await supabase.from('profiles').select('id, first_name, last_name, username')
      .neq('id', session.user.id)
      .or(`username.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%`)
      .limit(20)
    const results = data || []
    const sorted = [
      ...results.filter(p => friendIds.includes(p.id)),
      ...results.filter(p => !friendIds.includes(p.id)),
    ]
    setSearchResults(sorted.slice(0, 8))
  }

  const handleCreate = async () => {
    if (!opponent) return showToast('Select an opponent', 'error')
    if (!description.trim()) return showToast('Describe the bet', 'error')
    if (!wager || parseFloat(wager) <= 0) return showToast('Enter a wager amount', 'error')
    setSaving(true)
    const { error } = await supabase.from('one_v_one').insert({
      created_by: session.user.id,
      opponent_id: opponent.id,
      description: description.trim(),
      wager_amount: parseFloat(wager),
      settle_by: settleBy || null,
      status: 'pending',
    })
    setSaving(false)
    if (error) return showToast(error.message, 'error')
    showToast('Challenge sent!', 'success')
    setView('list')
    setDescription(''); setWager(''); setSettleBy(''); setOpponent(null); setSearch('')
    loadAll()
  }

  const handleAccept = async (bet) => {
    setActing(prev => ({ ...prev, [bet.id]: 'accepting' }))
    await supabase.from('one_v_one').update({ status: 'active', activated_at: new Date().toISOString() }).eq('id', bet.id)
    setActing(prev => ({ ...prev, [bet.id]: null }))
    showToast('Bet accepted!', 'success')
    loadAll()
  }

  const handleDecline = async (bet) => {
    setActing(prev => ({ ...prev, [bet.id]: 'declining' }))
    await supabase.from('one_v_one').update({ status: 'cancelled' }).eq('id', bet.id)
    setActing(prev => ({ ...prev, [bet.id]: null }))
    showToast('Bet declined', 'info')
    loadAll()
  }

  // Double-tap handler — key is betId + actionName
  const doubleTap = (betId, action, onConfirm) => {
    const key = `${betId}_${action}`
    if (pending[key]) {
      setPending(prev => ({ ...prev, [key]: false }))
      onConfirm()
    } else {
      setPending(prev => ({ ...prev, [key]: true }))
      setTimeout(() => setPending(prev => ({ ...prev, [key]: false })), 3000)
    }
  }

  const handleClaimWin = async (bet) => {
    // Guard against simultaneous claims
    const { data: fresh } = await supabase.from('one_v_one').select('status').eq('id', bet.id).single()
    if (fresh?.status !== 'active' && fresh?.status !== 'disputed') {
      showToast('Bet status has changed — refresh', 'error')
      loadAll(); return
    }
    setActing(prev => ({ ...prev, [bet.id]: 'claiming' }))
    const otherPlayer = bet.created_by === session.user.id ? bet.opponent_id : bet.created_by
        await supabase.from('one_v_one').update({ status: 'claimed', claimed_by: session.user.id, claimed_against: otherPlayer, action_by: session.user.id }).eq('id', bet.id)
    setActing(prev => ({ ...prev, [bet.id]: null }))
    showToast('Win claimed — waiting for opponent to confirm', 'success')
    loadAll()
  }

  const handleClaimLoss = async (bet) => {
    const { data: fresh } = await supabase.from('one_v_one').select('status').eq('id', bet.id).single()
    if (fresh?.status !== 'active' && fresh?.status !== 'disputed') {
      showToast('Bet status has changed — refresh', 'error')
      loadAll(); return
    }
    const other = bet.created_by === session.user.id ? bet.opponent_id : bet.created_by
    setActing(prev => ({ ...prev, [bet.id]: 'losing' }))
    await supabase.from('one_v_one').update({ status: 'claimed', claimed_by: other, claimed_against: session.user.id, action_by: session.user.id }).eq('id', bet.id)
    setActing(prev => ({ ...prev, [bet.id]: null }))
    showToast('Confirmed — waiting for opponent to accept', 'info')
    loadAll()
  }

  const handleAgree = async (bet) => {
    setActing(prev => ({ ...prev, [bet.id]: 'confirming' }))
    await supabase.from('one_v_one').update({ status: 'settled', confirmed_by: session.user.id }).eq('id', bet.id)
    setActing(prev => ({ ...prev, [bet.id]: null }))
    showToast('Bet settled!', 'success')
    loadAll()
  }

  const handleDisagree = async (bet) => {
    // Only set disputed_at if not already disputed (timer doesn't reset)
    const updateData = { status: 'disputed', claimed_by: null, claimed_against: null, action_by: null }
    if (!bet.disputed_at) updateData.disputed_at = new Date().toISOString()
    setActing(prev => ({ ...prev, [bet.id]: 'disputing' }))
    await supabase.from('one_v_one').update(updateData).eq('id', bet.id)
    setActing(prev => ({ ...prev, [bet.id]: null }))
    showToast('Disputed — 24hr timer started', 'info')
    loadAll()
  }

  const getDisputeTimeLeft = (disputedAt) => {
    if (!disputedAt) return null
    const end = new Date(disputedAt).getTime() + 24 * 60 * 60 * 1000
    const now = Date.now()
    const diff = end - now
    if (diff <= 0) return 'Expired'
    const h = Math.floor(diff / 3600000)
    const m = Math.floor((diff % 3600000) / 60000)
    return `${h}h ${m}m remaining`
  }

  const fmt = (pid) => profiles[pid] ? `${profiles[pid].first_name} ${profiles[pid].last_name || ''}`.trim() : '?'
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null
  const friendIds = friends.map(f => f.user_id === session.user.id ? f.friend_id : f.user_id)
  const statusColor = { pending: '#C19A6B', active: '#00563F', claimed: '#C19A6B', settled: '#7A7570', cancelled: '#7A7570', disputed: '#C0392B', voided: '#C0392B' }
  const statusLabel = { pending: 'PENDING', active: 'ACTIVE', claimed: 'CLAIMED', settled: '✓ SETTLED', cancelled: 'CANCELLED', disputed: '⚠ DISPUTED', voided: '✗ VOIDED' }

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#7A7570', fontFamily: 'DM Sans, sans-serif' }}>Loading...</div>
    </div>
  )

  return (
    <div style={s.screen}>
      {toast && <div style={{ ...s.toast, background: toast.type === 'error' ? '#C0392B' : toast.type === 'success' ? '#00563F' : '#1A1814' }}>{toast.msg}</div>}

      <div style={s.body}>
        {view === 'list' && (
          <>
            <button style={s.newBtn} onClick={() => setView('create')}>+ New 1v1 CROSSBET</button>

            {bets.length === 0 ? (
              <div style={s.empty}>
                <div style={s.emptyIcon}>⚔️</div>
                <div style={s.emptyText}>No 1v1 bets yet</div>
                <div style={s.emptySub}>Challenge anyone to a CROSSBET</div>
              </div>
            ) : bets.map(bet => {
              const isCreator = bet.created_by === session.user.id
              const other = isCreator ? bet.opponent_id : bet.created_by
              const claimedByOther = bet.status === 'claimed' && bet.action_by !== session.user.id
              const claimedByMe = bet.action_by === session.user.id
              const a = acting[bet.id]
              const isActive = bet.status === 'active'
              const isClaimed = bet.status === 'claimed'
              const isDisputed = bet.status === 'disputed'

              return (
                <div key={bet.id} style={s.betCard}>
                  <div style={s.betTop}>
                    <div style={s.betDesc}>{bet.description}</div>
                    <div style={{ ...s.betStatus, color: statusColor[bet.status] }}>{statusLabel[bet.status]}</div>
                  </div>
                  <div style={s.betMeta}>
                    <span style={s.metaLabel}>VS </span><span style={s.metaVal}>{fmt(other)}</span>
                    <span style={s.metaSep}>·</span>
                    <span style={s.wagerAmt}>${bet.wager_amount}</span>
                    {bet.settle_by && <><span style={s.metaSep}>·</span><span style={s.metaLabel}>BY </span><span style={s.metaVal}>{fmtDate(bet.settle_by)}</span></>}
                  </div>

                  {/* Pending — accept/decline */}
                  {bet.status === 'pending' && !isCreator && (
                    <div style={s.actions}>
                      <button style={{ ...s.acceptBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleAccept(bet)} disabled={!!a}>
                        {a === 'accepting' ? '...' : '✓ Accept'}
                      </button>
                      <button style={{ ...s.declineBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleDecline(bet)} disabled={!!a}>
                        {a === 'declining' ? '...' : 'Decline'}
                      </button>
                    </div>
                  )}
                  {bet.status === 'pending' && isCreator && (
                    <div style={s.waitingNote}>Waiting for {fmt(other)} to accept</div>
                  )}

                  {/* Active or Disputed — Won/Lost buttons */}
                  {(isActive || isDisputed) && (
                    <div style={s.actions}>
                      <button
                        style={{ ...s.wonBtn, ...(pending[`${bet.id}_won`] ? s.confirmingBtn : {}), opacity: a ? 0.7 : 1 }}
                        onClick={() => doubleTap(bet.id, 'won', () => handleClaimWin(bet))}
                        disabled={!!a}>
                        {pending[`${bet.id}_won`] ? 'Confirm Win?' : '🏆 I Won'}
                      </button>
                      <button
                        style={{ ...s.lostBtn, ...(pending[`${bet.id}_lost`] ? s.confirmingLostBtn : {}), opacity: a ? 0.7 : 1 }}
                        onClick={() => doubleTap(bet.id, 'lost', () => handleClaimLoss(bet))}
                        disabled={!!a}>
                        {pending[`${bet.id}_lost`] ? 'Confirm Loss?' : 'I Lost'}
                      </button>
                    </div>
                  )}

                  {/* Disputed timer */}
                  {isDisputed && bet.disputed_at && (
                    <div style={s.disputedNote}>
                      ⚠ Disputed · {getDisputeTimeLeft(bet.disputed_at)}
                    </div>
                  )}

                  {/* Claimed — waiting or agree/disagree */}
                  {isClaimed && claimedByMe && (
                    <div style={s.waitingNote}>{bet.action_by === bet.claimed_by ? 'Win' : 'Loss'} claimed — waiting for {fmt(other)} to confirm</div>
                  )}
                  {isClaimed && claimedByOther && (
                    <div style={s.claimAlert}>
                      <div style={s.claimAlertText}>{fmt(other)} claims {bet.action_by === bet.claimed_by ? 'a win' : 'a loss'} — do you agree?</div>
                      <div style={s.claimActions}>
                        <button
                          style={{ ...s.acceptBtn, ...(pending[`${bet.id}_agree`] ? s.confirmingBtn : {}), opacity: a ? 0.7 : 1 }}
                          onClick={() => doubleTap(bet.id, 'agree', () => handleAgree(bet))}
                          disabled={!!a}>
                          {pending[`${bet.id}_agree`] ? 'Confirm?' : '✓ Agree'}
                        </button>
                        <button
                          style={{ ...s.disputeBtn, ...(pending[`${bet.id}_disagree`] ? s.confirmingLostBtn : {}), opacity: a ? 0.7 : 1 }}
                          onClick={() => doubleTap(bet.id, 'disagree', () => handleDisagree(bet))}
                          disabled={!!a}>
                          {pending[`${bet.id}_disagree`] ? 'Confirm?' : '✗ Disagree'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </>
        )}

        {view === 'create' && (
          <>
            <div style={s.sectionTitle}>THE BET</div>
            <textarea style={s.textarea} placeholder="e.g. Messi scores next goal · Lakers win tonight"
              value={description} onChange={e => setDescription(e.target.value)} rows={3} />

            <div style={s.sectionTitle}>CHALLENGE</div>
            {opponent ? (
              <div style={s.opponentCard}>
                <div style={s.opponentName}>{opponent.first_name} {opponent.last_name}</div>
                <div style={s.opponentUser}>@{opponent.username}</div>
                <button style={s.changeBtn} onClick={() => setOpponent(null)}>Change</button>
              </div>
            ) : (
              <>

                <input style={s.input} placeholder="Search by name or @username"
                  value={search} onChange={e => handleSearch(e.target.value)} />
                {searchResults.length > 0 && (
                  <div style={s.searchResults}>
                    {searchResults.map(p => (
                      <button key={p.id} style={s.searchResult} onClick={() => { setOpponent(p); setSearch(''); setSearchResults([]) }}>
                        <div style={s.searchName}>{p.first_name} {p.last_name}{friendIds.includes(p.id) ? <span style={{ fontSize: 10, color: '#00563F', fontWeight: 700, marginLeft: 6 }}>★ FRIEND</span> : ''}</div>
                        <div style={s.searchUser}>@{p.username}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            <div style={s.sectionTitle}>WAGER</div>
            <div style={s.wagerRow}>
              <span style={s.wagerSign}>$</span>
              <input style={s.wagerInput} type="number" min="1" placeholder="0"
                value={wager} onChange={e => setWager(e.target.value)} />
            </div>

            <div style={s.sectionTitle}>EVENT DATE & TIME <span style={s.optional}>(optional)</span></div>
            <input style={s.input} type="datetime-local" value={settleBy} min={new Date().toISOString().slice(0,16)} onChange={e => setSettleBy(e.target.value)} />

            <div style={s.formBtns}>
              <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={handleCreate} disabled={saving}>
                {saving ? 'Sending...' : 'Send Challenge →'}
              </button>
              <button style={s.cancelBtn} onClick={() => { setView('list'); setOpponent(null); setSearch(''); setDescription(''); setWager(''); setSettleBy(''); setSearchResults([]) }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  toast: { position: 'fixed', top: 0, left: 0, right: 0, padding: '16px 24px', color: 'white', fontSize: 14, fontWeight: 600, textAlign: 'center', zIndex: 100 },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 12 },
  newBtn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' },
  empty: { background: '#FFFFFF', borderRadius: 20, padding: 36, border: '1px solid #E0DDD4', textAlign: 'center' },
  emptyIcon: { fontSize: 36, marginBottom: 10 },
  emptyText: { fontSize: 15, fontWeight: 700, color: '#1A1814', marginBottom: 4 },
  emptySub: { fontSize: 13, color: '#7A7570' },
  betCard: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 10 },
  betTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
  betDesc: { fontSize: 15, fontWeight: 700, color: '#1A1814', flex: 1 },
  betStatus: { fontSize: 10, fontWeight: 700, letterSpacing: 1, flexShrink: 0 },
  betMeta: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  metaLabel: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1 },
  metaVal: { fontSize: 13, color: '#1A1814', fontWeight: 500 },
  metaSep: { color: '#D0CCC0' },
  wagerAmt: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: '#C19A6B' },
  actions: { display: 'flex', gap: 8 },
  acceptBtn: { flex: 1, background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  declineBtn: { flex: 1, background: 'transparent', color: '#7A7570', border: '1.5px solid #E0DDD4', borderRadius: 12, padding: '12px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' },
  wonBtn: { flex: 1, background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  lostBtn: { flex: 1, background: 'transparent', color: '#7A7570', border: '1.5px solid #E0DDD4', borderRadius: 12, padding: '13px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' },
  confirmingBtn: { background: 'linear-gradient(135deg,#00563F,#006B4D)' },
  confirmingLostBtn: { background: '#C0392B', color: 'white', border: '1px solid #C0392B' },
  claimAlert: { background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 12, padding: '12px', display: 'flex', flexDirection: 'column', gap: 8 },
  claimAlertText: { fontSize: 13, fontWeight: 600, color: '#9B7D52' },
  claimActions: { display: 'flex', gap: 8 },
  disputeBtn: { flex: 1, background: 'transparent', border: '1px solid #C0392B', borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 700, color: '#C0392B', cursor: 'pointer', fontFamily: 'inherit' },
  disputedNote: { fontSize: 12, color: '#C0392B', fontWeight: 600, background: 'rgba(192,57,43,0.06)', border: '1px solid rgba(192,57,43,0.15)', borderRadius: 10, padding: '8px 12px' },
  waitingNote: { fontSize: 12, color: '#7A7570', fontStyle: 'italic' },
  sectionTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 16, color: '#1A1814', letterSpacing: 1, marginTop: 4 },
  optional: { fontSize: 12, fontWeight: 400, color: '#B0ACA4', letterSpacing: 0, fontFamily: "'DM Sans', sans-serif" },
  textarea: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 16px', fontSize: 15, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'none', lineHeight: 1.5 },
  opponentCard: { background: '#FFFFFF', borderRadius: 16, padding: '12px 16px', border: '1.5px solid #00563F', display: 'flex', alignItems: 'center', gap: 10 },
  opponentName: { fontSize: 15, fontWeight: 700, color: '#1A1814', flex: 1 },
  opponentUser: { fontSize: 12, color: '#7A7570' },
  changeBtn: { background: 'transparent', border: 'none', color: '#00563F', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  friendsLabel: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5 },
  input: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 16px', fontSize: 15, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  searchResults: { background: '#FFFFFF', borderRadius: 12, border: '1px solid #E0DDD4', overflow: 'hidden' },
  searchResult: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'none', border: 'none', borderTop: '1px solid #EDE8D8', cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'left' },
  searchName: { fontSize: 14, fontWeight: 600, color: '#1A1814' },
  searchUser: { fontSize: 12, color: '#7A7570' },
  wagerRow: { position: 'relative' },
  wagerSign: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: '#C19A6B' },
  wagerInput: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 16px 13px 36px', fontSize: 20, fontFamily: "'Bebas Neue', sans-serif", color: '#9B7D52', outline: 'none', width: '100%', boxSizing: 'border-box' },
  formBtns: { display: 'flex', gap: 8, marginTop: 4 },
  saveBtn: { flex: 1, background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  cancelBtn: { background: 'transparent', border: '1.5px solid #E0DDD4', borderRadius: 12, padding: '14px 20px', fontSize: 14, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
}
